import Swiper from 'swiper';
import { Navigation, Autoplay, Keyboard } from 'swiper/modules';

// Wait for DOM to be ready
function initMain() {
	// Cache document.body
	const body = document.body;
	if (!body) return;

	// Header scrolled class on scroll
	const siteHeader = document.querySelector('.site-header');
	if (siteHeader) {
		// Set CSS variable for header height once (used by mobile menu only)
		const setHeaderHeight = () => {
			const headerHeight = siteHeader.offsetHeight;
			document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
		};
		
		// Set on initial load
		setHeaderHeight();
		
		// Update only on window resize (not during scroll/animations)
		let resizeTimeout;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(setHeaderHeight, 150);
		});
		
		window.addEventListener('orientationchange', () => {
			setTimeout(setHeaderHeight, 150);
		});

		// Scroll handler with debounce to prevent flickering
		let ticking = false;
		let debounceTimeout;
		const SCROLL_THRESHOLD = 24;
		const DEBOUNCE_DELAY = 50; // Small delay to prevent rapid toggling
		
		const handleScroll = () => {
			const scrollY = window.scrollY || window.pageYOffset;
			const isScrolled = siteHeader.classList.contains('scrolled');
			const shouldBeScrolled = scrollY > SCROLL_THRESHOLD;

			// Only change class if state needs to change
			if (shouldBeScrolled !== isScrolled) {
				clearTimeout(debounceTimeout);
				debounceTimeout = setTimeout(() => {
					if (shouldBeScrolled) {
						siteHeader.classList.add('scrolled');
					} else {
						siteHeader.classList.remove('scrolled');
					}
				}, DEBOUNCE_DELAY);
			}
			
			ticking = false;
		};

		// Throttle scroll events with requestAnimationFrame
		const onScroll = () => {
			if (!ticking) {
				requestAnimationFrame(handleScroll);
				ticking = true;
			}
		};

		// Check on initial load
		handleScroll();

		// Check on scroll
		window.addEventListener('scroll', onScroll, { passive: true });
	}

	// Mobile Menu Collapse
	const mobileMenuTrigger = document.querySelector('.site-header .menu-trigger');
	const mobileMenu = document.querySelector('.site-header .navigation-panel');
	if (mobileMenuTrigger && mobileMenu) {
		mobileMenuTrigger.addEventListener('click', function() {
			mobileMenu.classList.toggle('shown');
			mobileMenuTrigger.classList.toggle('is-active');
			body.classList.toggle('overflow-y-hidden');
		});
	}

	// Search form clear functionality
	const searchForms = document.querySelectorAll('.search-form');
	searchForms.forEach((form) => {
		const searchField = form.querySelector('.search-field');
		const clearButton = form.querySelector('.search-clear');
		
		if (!searchField || !clearButton) return;

		// Function to toggle clear button visibility
		const toggleClearButton = () => {
			if (searchField.value && searchField.value.trim() !== '') {
				clearButton.classList.add('visible');
			} else {
				clearButton.classList.remove('visible');
			}
		};

		// Initial check
		toggleClearButton();

		// Show/hide clear button on input
		searchField.addEventListener('input', toggleClearButton);
		searchField.addEventListener('keyup', toggleClearButton);

		// Clear input on button click
		clearButton.addEventListener('click', () => {
			searchField.value = '';
			searchField.focus();
			clearButton.classList.remove('visible');
		});
	});

	// Glow-in on scroll: add .animate to .glow-in elements when they enter the viewport
	const glowInElements = document.querySelectorAll('.glow-in');
	if (glowInElements.length && 'IntersectionObserver' in window) {
		const glowInObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('animate');
						glowInObserver.unobserve(entry.target);
					}
				});
			},
			{ rootMargin: '0px 0px -10% 0px', threshold: 0 }
		);
		glowInElements.forEach((el) => glowInObserver.observe(el));
	}

	// Kennisbank filters and load more
	initKennisbank();

	// Merken filters and load more
	initMerken();

	// Markten filters and load more
	initMarkten();

	// Portfolio filters and load more
	initPortfolio();

	// Initialize Markt slider gallery Swiper
	initSliderGalleryMarkt();

	// Initialize Tabbed Sliders
	initTabbedSliders();

	// Initialize Collapsible Content
	initCollapsibleContent();

	// Initialize Gallery Modal with Swiper
	initGalleryModal();

	// Case gallery slider (new_style layout)
	initCaseGallerySlider();

	// Logos slider (autoplay, loop, no nav/pagination)
	initLogosSlider();
}

/**
 * Initialize Kennisbank page functionality
 */
function initKennisbank() {
	const filtersContainer = document.querySelector('.kennisbank-filters');
	const filterSelect = document.getElementById('kennisbank-filter-select');
	const postsContainer = document.getElementById('kennisbank-posts');
	const loadMoreBtn = document.getElementById('load-more-btn');
	
	if (!postsContainer) return;

	let currentCategory = '';
	let currentPage = 1;
	let isLoading = false;

	// Animate initial cards on page load
	animateCards(postsContainer.querySelectorAll('.post-card'));

	const setActiveFilterButton = (category) => {
		if (!filtersContainer) return;
		const buttons = filtersContainer.querySelectorAll('.filter-btn');
		buttons.forEach(b => {
			b.classList.remove('active');
		});

		const activeBtn = Array.from(buttons).find(btn => (btn.dataset.category || '') === category);
		if (activeBtn) {
			activeBtn.classList.add('active');
		}
	};

	// Filter buttons click handler (desktop)
	if (filtersContainer) {
		filtersContainer.addEventListener('click', (e) => {
			const btn = e.target.closest('.filter-btn');
			if (!btn || isLoading) return;

			// Load posts for selected category
			currentCategory = btn.dataset.category || '';
			currentPage = 1;
			setActiveFilterButton(currentCategory);
			if (filterSelect) {
				filterSelect.value = currentCategory;
			}
			loadPosts(currentCategory, currentPage, false);
		});
	}

	// Filter select change handler (mobile)
	const handleSelectChange = () => {
		if (isLoading) return;
		currentCategory = filterSelect.value || '';
		currentPage = 1;
		setActiveFilterButton(currentCategory);
		loadPosts(currentCategory, currentPage, false);
	};

	if (filterSelect) {
		filterSelect.addEventListener('change', handleSelectChange);
		filterSelect.addEventListener('input', handleSelectChange);
		filterSelect.addEventListener('blur', handleSelectChange);
	}

	// Load more button click handler
	if (loadMoreBtn) {
		loadMoreBtn.addEventListener('click', () => {
			if (isLoading) return;
			currentPage++;
			loadPosts(currentCategory, currentPage, true);
		});
	}

	/**
	 * Animate cards with stagger effect
	 * @param {NodeList} cards - Cards to animate
	 * @param {number} startIndex - Starting index for stagger delay
	 */
	function animateCards(cards, startIndex = 0) {
		cards.forEach((card, index) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(20px)';
			
			setTimeout(() => {
				card.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
				card.style.opacity = '1';
				card.style.transform = 'translateY(0)';
			}, (startIndex + index) * 80);
		});
	}

	/**
	 * Fade out existing cards
	 * @returns {Promise} - Resolves when animation completes
	 */
	function fadeOutCards() {
		return new Promise(resolve => {
			const cards = postsContainer.querySelectorAll('.post-card');
			if (cards.length === 0) {
				resolve();
				return;
			}

			cards.forEach(card => {
				card.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
				card.style.opacity = '0';
				card.style.transform = 'translateY(-10px)';
			});

			setTimeout(resolve, 200);
		});
	}

	/**
	 * Load posts via AJAX
	 * @param {string} category - Category slug
	 * @param {number} page - Page number
	 * @param {boolean} append - Append to existing posts or replace
	 */
	async function loadPosts(category, page, append) {
		if (isLoading) return;
		
		isLoading = true;

		// Preserve scroll position when appending
		const shouldPreserveScroll = append;
		const preservedScrollY = shouldPreserveScroll ? window.scrollY : null;
		
		// Show loading state
		if (loadMoreBtn) {
			loadMoreBtn.disabled = true;
			loadMoreBtn.classList.add('opacity-50');
		}
		
		if (!append) {
			await fadeOutCards();
		}

		try {
			const formData = new FormData();
			formData.append('action', 'load_kennisbank_posts');
			formData.append('nonce', window.kennisbankAjax?.nonce || '');
			formData.append('category', category);
			formData.append('page', page);
			formData.append('per_page', loadMoreBtn?.dataset.perPage || 12);

			const response = await fetch(window.kennisbankAjax?.ajaxUrl || '/wp-admin/admin-ajax.php', {
				method: 'POST',
				body: formData
			});

			const data = await response.json();

			if (data.success) {
				if (append) {
					postsContainer.insertAdjacentHTML('beforeend', data.data.html);
				} else {
					postsContainer.innerHTML = data.data.html || '<p class="text-white/60 text-center col-span-2">Geen artikelen gevonden.</p>';
				}

				// Animate new cards
				const allCards = postsContainer.querySelectorAll('.post-card');
				if (append) {
					// Only animate newly added cards
					const existingAnimated = postsContainer.querySelectorAll('.post-card[style*="opacity: 1"]').length;
					const newCards = Array.from(allCards).slice(existingAnimated);
					animateCards(newCards, 0);
				} else {
					animateCards(allCards);
				}

				// Update load more button visibility
				if (loadMoreBtn) {
					loadMoreBtn.dataset.page = page;
					loadMoreBtn.dataset.category = category;
					
					if (data.data.has_more) {
						loadMoreBtn.closest('.kennisbank-load-more').classList.remove('hidden');
					} else {
						loadMoreBtn.closest('.kennisbank-load-more').classList.add('hidden');
					}
				}

				// Restore scroll position after DOM update (mobile)
				if (shouldPreserveScroll && preservedScrollY !== null) {
					requestAnimationFrame(() => {
						window.scrollTo({ top: preservedScrollY, behavior: 'auto' });
						if (loadMoreBtn) {
							loadMoreBtn.focus({ preventScroll: true });
						}
					});
				}
			}
		} catch (error) {
			console.error('Error loading posts:', error);
		} finally {
			isLoading = false;
			
			if (loadMoreBtn) {
				loadMoreBtn.disabled = false;
				loadMoreBtn.classList.remove('opacity-50');
			}
		}
	}
}

/**
 * Initialize Merken page functionality
 */
function initMerken() {
	const filtersContainer = document.querySelector('.merken-filters');
	const filterSelect = document.getElementById('merken-filter-select');
	const postsContainer = document.getElementById('merken-posts');
	const loadMoreBtn = document.getElementById('merken-load-more-btn');
	
	if (!postsContainer) return;

	// Get default category from first active button or from select
	let currentCategory = '';
	if (filtersContainer) {
		const activeBtn = filtersContainer.querySelector('.filter-btn.active');
		currentCategory = activeBtn ? (activeBtn.dataset.category || '') : '';
	} else if (filterSelect) {
		currentCategory = filterSelect.value || '';
	}
	
	let currentPage = 1;
	let isLoading = false;

	// Animate initial cards on page load
	animateCards(postsContainer.querySelectorAll('.merk-card'));

	const setActiveFilterButton = (category) => {
		if (!filtersContainer) return;
		const buttons = filtersContainer.querySelectorAll('.filter-btn');
		buttons.forEach(b => {
			b.classList.remove('active');
		});

		const activeBtn = Array.from(buttons).find(btn => (btn.dataset.category || '') === category);
		if (activeBtn) {
			activeBtn.classList.add('active');
		}
	};

	// Filter buttons click handler (desktop)
	if (filtersContainer) {
		filtersContainer.addEventListener('click', (e) => {
			const btn = e.target.closest('.filter-btn');
			if (!btn || isLoading) return;

			// Load posts for selected category
			currentCategory = btn.dataset.category || '';
			currentPage = 1;
			setActiveFilterButton(currentCategory);
			if (filterSelect) {
				filterSelect.value = currentCategory;
			}
			loadPosts(currentCategory, currentPage, false);
		});
	}

	// Filter select change handler (mobile)
	const handleSelectChange = () => {
		if (isLoading) return;
		currentCategory = filterSelect.value || '';
		currentPage = 1;
		setActiveFilterButton(currentCategory);
		loadPosts(currentCategory, currentPage, false);
	};

	if (filterSelect) {
		filterSelect.addEventListener('change', handleSelectChange);
		filterSelect.addEventListener('input', handleSelectChange);
		filterSelect.addEventListener('blur', handleSelectChange);
	}

	// Load more button click handler
	if (loadMoreBtn) {
		loadMoreBtn.addEventListener('click', () => {
			if (isLoading) return;
			currentPage++;
			loadPosts(currentCategory, currentPage, true);
		});
	}

	/**
	 * Animate cards with stagger effect
	 * @param {NodeList} cards - Cards to animate
	 * @param {number} startIndex - Starting index for stagger delay
	 */
	function animateCards(cards, startIndex = 0) {
		cards.forEach((card, index) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(20px)';
			
			setTimeout(() => {
				card.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
				card.style.opacity = '1';
				card.style.transform = 'translateY(0)';
			}, (startIndex + index) * 80);
		});
	}

	/**
	 * Fade out existing cards
	 * @returns {Promise} - Resolves when animation completes
	 */
	function fadeOutCards() {
		return new Promise(resolve => {
			const cards = postsContainer.querySelectorAll('.merk-card');
			if (cards.length === 0) {
				resolve();
				return;
			}

			cards.forEach(card => {
				card.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
				card.style.opacity = '0';
				card.style.transform = 'translateY(-10px)';
			});

			setTimeout(resolve, 200);
		});
	}

	/**
	 * Load posts via AJAX
	 * @param {string} category - Category slug
	 * @param {number} page - Page number
	 * @param {boolean} append - Append to existing posts or replace
	 */
	async function loadPosts(category, page, append) {
		if (isLoading) return;
		
		isLoading = true;

		// Preserve scroll position when appending
		const shouldPreserveScroll = append;
		const preservedScrollY = shouldPreserveScroll ? window.scrollY : null;
		
		// Show loading state
		if (loadMoreBtn) {
			loadMoreBtn.disabled = true;
			loadMoreBtn.classList.add('opacity-50');
		}
		
		if (!append) {
			await fadeOutCards();
		}

		try {
			const formData = new FormData();
			formData.append('action', 'load_merken_posts');
			formData.append('nonce', window.merkenAjax?.nonce || '');
			formData.append('category', category);
			formData.append('page', page);
			formData.append('per_page', loadMoreBtn?.dataset.perPage || 12);

			const response = await fetch(window.merkenAjax?.ajaxUrl || '/wp-admin/admin-ajax.php', {
				method: 'POST',
				body: formData
			});

			const data = await response.json();

			if (data.success) {
				if (append) {
					postsContainer.insertAdjacentHTML('beforeend', data.data.html);
				} else {
					postsContainer.innerHTML = data.data.html || '<p class="text-white/60 text-center col-span-3">Geen merken gevonden.</p>';
				}

				// Animate new cards
				const allCards = postsContainer.querySelectorAll('.merk-card');
				if (append) {
					// Only animate newly added cards
					const existingAnimated = postsContainer.querySelectorAll('.merk-card[style*="opacity: 1"]').length;
					const newCards = Array.from(allCards).slice(existingAnimated);
					animateCards(newCards, 0);
				} else {
					animateCards(allCards);
				}

				// Update load more button visibility
				if (loadMoreBtn) {
					loadMoreBtn.dataset.page = page;
					loadMoreBtn.dataset.category = category;
					
					if (data.data.has_more) {
						loadMoreBtn.closest('.merken-load-more').classList.remove('hidden');
					} else {
						loadMoreBtn.closest('.merken-load-more').classList.add('hidden');
					}
				}

				// Restore scroll position after DOM update (mobile)
				if (shouldPreserveScroll && preservedScrollY !== null) {
					requestAnimationFrame(() => {
						window.scrollTo({ top: preservedScrollY, behavior: 'auto' });
						if (loadMoreBtn) {
							loadMoreBtn.focus({ preventScroll: true });
						}
					});
				}
			}
		} catch (error) {
			console.error('Error loading posts:', error);
		} finally {
			isLoading = false;
			
			if (loadMoreBtn) {
				loadMoreBtn.disabled = false;
				loadMoreBtn.classList.remove('opacity-50');
			}
		}
	}
}

/**
 * Initialize Portfolio page functionality (case_categories + case)
 */
function initPortfolio() {
	const filtersContainer = document.querySelector('.portfolio-filters');
	const filterSelect = document.getElementById('portfolio-filter-select');
	const postsContainer = document.getElementById('portfolio-posts');
	const loadMoreBtn = document.getElementById('portfolio-load-more-btn');

	if (!postsContainer) return;

	let currentCategory = '';
	if (filtersContainer) {
		const activeBtn = filtersContainer.querySelector('.filter-btn.active');
		currentCategory = activeBtn ? (activeBtn.dataset.category || '') : '';
	} else if (filterSelect) {
		currentCategory = filterSelect.value || '';
	}

	let currentPage = 1;
	let isLoading = false;

	animateCards(postsContainer.querySelectorAll('.case-card'));

	const setActiveFilterButton = (category) => {
		if (!filtersContainer) return;
		const buttons = filtersContainer.querySelectorAll('.filter-btn');
		buttons.forEach(b => b.classList.remove('active'));
		const activeBtn = Array.from(buttons).find(btn => (btn.dataset.category || '') === category);
		if (activeBtn) activeBtn.classList.add('active');
	};

	if (filtersContainer) {
		filtersContainer.addEventListener('click', (e) => {
			const btn = e.target.closest('.filter-btn');
			if (!btn || isLoading) return;
			currentCategory = btn.dataset.category || '';
			currentPage = 1;
			setActiveFilterButton(currentCategory);
			if (filterSelect) filterSelect.value = currentCategory;
			loadPosts(currentCategory, currentPage, false);
		});
	}

	const handleSelectChange = () => {
		if (isLoading) return;
		currentCategory = filterSelect ? filterSelect.value || '' : '';
		currentPage = 1;
		setActiveFilterButton(currentCategory);
		loadPosts(currentCategory, currentPage, false);
	};

	if (filterSelect) {
		filterSelect.addEventListener('change', handleSelectChange);
		filterSelect.addEventListener('input', handleSelectChange);
		filterSelect.addEventListener('blur', handleSelectChange);
	}

	if (loadMoreBtn) {
		loadMoreBtn.addEventListener('click', () => {
			if (isLoading) return;
			currentPage++;
			loadPosts(currentCategory, currentPage, true);
		});
	}

	function animateCards(cards, startIndex = 0) {
		cards.forEach((card, index) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(20px)';
			setTimeout(() => {
				card.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
				card.style.opacity = '1';
				card.style.transform = 'translateY(0)';
			}, (startIndex + index) * 80);
		});
	}

	function fadeOutCards() {
		return new Promise(resolve => {
			const cards = postsContainer.querySelectorAll('.case-card');
			if (cards.length === 0) {
				resolve();
				return;
			}
			cards.forEach(card => {
				card.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
				card.style.opacity = '0';
				card.style.transform = 'translateY(-10px)';
			});
			setTimeout(resolve, 200);
		});
	}

	async function loadPosts(category, page, append) {
		if (isLoading) return;
		isLoading = true;

		const shouldPreserveScroll = append;
		const preservedScrollY = shouldPreserveScroll ? window.scrollY : null;

		if (loadMoreBtn) {
			loadMoreBtn.disabled = true;
			loadMoreBtn.classList.add('opacity-50');
		}

		if (!append) {
			await fadeOutCards();
		}

		try {
			const formData = new FormData();
			formData.append('action', 'load_portfolio_posts');
			formData.append('nonce', window.portfolioAjax?.nonce || '');
			formData.append('category', category);
			formData.append('page', page);
			formData.append('per_page', loadMoreBtn?.dataset.perPage || 12);

			const response = await fetch(window.portfolioAjax?.ajaxUrl || '/wp-admin/admin-ajax.php', {
				method: 'POST',
				body: formData
			});

			const data = await response.json();

			if (data.success) {
				if (append) {
					postsContainer.insertAdjacentHTML('beforeend', data.data.html);
				} else {
					postsContainer.innerHTML = data.data.html || '<p class="text-white/60 text-center col-span-3">Geen cases gevonden.</p>';
				}

				const allCards = postsContainer.querySelectorAll('.case-card');
				if (append) {
					const existingAnimated = postsContainer.querySelectorAll('.case-card[style*="opacity: 1"]').length;
					const newCards = Array.from(allCards).slice(existingAnimated);
					animateCards(newCards, 0);
				} else {
					animateCards(allCards);
				}

				if (loadMoreBtn) {
					loadMoreBtn.dataset.page = page;
					loadMoreBtn.dataset.category = category;
					if (data.data.has_more) {
						loadMoreBtn.closest('.portfolio-load-more').classList.remove('hidden');
					} else {
						loadMoreBtn.closest('.portfolio-load-more').classList.add('hidden');
					}
				}

				if (shouldPreserveScroll && preservedScrollY !== null) {
					requestAnimationFrame(() => {
						window.scrollTo({ top: preservedScrollY, behavior: 'auto' });
						if (loadMoreBtn) loadMoreBtn.focus({ preventScroll: true });
					});
				}
			}
		} catch (error) {
			console.error('Error loading portfolio posts:', error);
		} finally {
			isLoading = false;
			if (loadMoreBtn) {
				loadMoreBtn.disabled = false;
				loadMoreBtn.classList.remove('opacity-50');
			}
		}
	}
}

/**
 * Initialize Markten page functionality
 */
function initMarkten() {
	const filtersContainer = document.querySelector('.markten-filters');
	const filterSelect = document.getElementById('markten-filter-select');
	const postsContainer = document.getElementById('markten-posts');
	const loadMoreBtn = document.getElementById('markten-load-more-btn');
	
	if (!postsContainer) return;

	// Get default category from first active button or from select
	let currentCategory = '';
	if (filtersContainer) {
		const activeBtn = filtersContainer.querySelector('.filter-btn.active');
		currentCategory = activeBtn ? (activeBtn.dataset.category || '') : '';
	} else if (filterSelect) {
		currentCategory = filterSelect.value || '';
	}
	
	let currentPage = 1;
	let isLoading = false;

	// Animate initial cards on page load
	animateCards(postsContainer.querySelectorAll('.markt-card'));

	const descriptionContainer = document.getElementById('markten-description');
	
	const updateDescription = (category) => {
		if (!descriptionContainer) return;
		
		let description = '';
		let descriptionHidden = '';
		
		// Get description from button
		if (filtersContainer) {
			const activeBtn = filtersContainer.querySelector(`.filter-btn[data-category="${category}"]`);
			if (activeBtn) {
				if (activeBtn.dataset.description) {
					try {
						description = JSON.parse(activeBtn.dataset.description);
					} catch (e) {
						description = activeBtn.dataset.description;
					}
				}
				if (activeBtn.dataset.descriptionHidden) {
					try {
						descriptionHidden = JSON.parse(activeBtn.dataset.descriptionHidden);
					} catch (e) {
						descriptionHidden = activeBtn.dataset.descriptionHidden;
					}
				}
			}
		}
		
		// Get description from select option
		if ((!description && !descriptionHidden) && filterSelect) {
			const selectedOption = filterSelect.querySelector(`option[value="${category}"]`);
			if (selectedOption) {
				if (selectedOption.dataset.description) {
					try {
						description = JSON.parse(selectedOption.dataset.description);
					} catch (e) {
						description = selectedOption.dataset.description;
					}
				}
				if (selectedOption.dataset.descriptionHidden) {
					try {
						descriptionHidden = JSON.parse(selectedOption.dataset.descriptionHidden);
					} catch (e) {
						descriptionHidden = selectedOption.dataset.descriptionHidden;
					}
				}
			}
		}
		
		if (description || descriptionHidden) {
			// Generate unique ID for collapsible content
			const uniqueId = 'markten-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
			
			let html = '<div class="collapsible-content-wrapper post-content leading-loose text-white/60">';
			
			if (description) {
				html += `<div class="collapsible-visible-content text-content">${description}</div>`;
			}
			
			if (descriptionHidden) {
				html += `<div class="collapsible-hidden-content hidden text-content mt-8" id="collapsible-hidden-${uniqueId}">${descriptionHidden}</div>`;
			}
			
			if (descriptionHidden) {
				html += `
					<div class="collapsible-toggle-wrapper mt-6">
						<button 
							type="button" 
							class="btn btn-semi-transparent arrow-down collapsible-toggle-btn"
							data-target="collapsible-hidden-${uniqueId}"
							aria-expanded="false"
						>
							<span class="btn-text-more">Meer lezen</span>
							<span class="btn-text-less hidden">Minder lezen</span>
						</button>
					</div>
				`;
			}
			
			html += '</div><!-- .collapsible-content-wrapper -->';
			
			descriptionContainer.innerHTML = html;
			descriptionContainer.style.display = 'block';
			
			// Use global initCollapsibleContent to initialize buttons
			// This ensures consistent behavior and prevents duplicate handlers
			if (typeof initCollapsibleContent === 'function') {
				// Use setTimeout to ensure DOM is fully updated
				setTimeout(() => {
					initCollapsibleContent();
				}, 0);
			}
		} else {
			descriptionContainer.style.display = 'none';
		}
	};

	const setActiveFilterButton = (category) => {
		if (!filtersContainer) return;
		const buttons = filtersContainer.querySelectorAll('.filter-btn');
		buttons.forEach(b => {
			b.classList.remove('active');
		});

		const activeBtn = Array.from(buttons).find(btn => (btn.dataset.category || '') === category);
		if (activeBtn) {
			activeBtn.classList.add('active');
		}

		// Update description
		updateDescription(category);

		// Switch category banner
		document.querySelectorAll('section.banner[data-category]').forEach(banner => {
			if (banner.dataset.category === category) {
				banner.classList.remove('hidden');
			} else {
				banner.classList.add('hidden');
			}
		});
	};

	// Initialize description for default category
	if (currentCategory && descriptionContainer) {
		updateDescription(currentCategory);
	}

	// Filter buttons click handler (desktop)
	if (filtersContainer) {
		filtersContainer.addEventListener('click', (e) => {
			const btn = e.target.closest('.filter-btn');
			if (!btn || isLoading) return;

			// Load posts for selected category
			currentCategory = btn.dataset.category || '';
			currentPage = 1;
			setActiveFilterButton(currentCategory);
			if (filterSelect) {
				filterSelect.value = currentCategory;
			}
			loadPosts(currentCategory, currentPage, false);
		});
	}

	// Filter select change handler (mobile)
	const handleSelectChange = () => {
		if (isLoading) return;
		currentCategory = filterSelect.value || '';
		currentPage = 1;
		setActiveFilterButton(currentCategory);
		loadPosts(currentCategory, currentPage, false);
	};

	if (filterSelect) {
		filterSelect.addEventListener('change', handleSelectChange);
		filterSelect.addEventListener('input', handleSelectChange);
		filterSelect.addEventListener('blur', handleSelectChange);
	}

	// Load more button click handler
	if (loadMoreBtn) {
		loadMoreBtn.addEventListener('click', () => {
			if (isLoading) return;
			currentPage++;
			loadPosts(currentCategory, currentPage, true);
		});
	}

	/**
	 * Animate cards with stagger effect
	 * @param {NodeList} cards - Cards to animate
	 * @param {number} startIndex - Starting index for stagger delay
	 */
	function animateCards(cards, startIndex = 0) {
		cards.forEach((card, index) => {
			card.style.opacity = '0';
			card.style.transform = 'translateY(20px)';
			
			setTimeout(() => {
				card.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
				card.style.opacity = '1';
				card.style.transform = 'translateY(0)';
			}, (startIndex + index) * 80);
		});
	}

	/**
	 * Fade out existing cards
	 * @returns {Promise} - Resolves when animation completes
	 */
	function fadeOutCards() {
		return new Promise(resolve => {
			const cards = postsContainer.querySelectorAll('.markt-card');
			if (cards.length === 0) {
				resolve();
				return;
			}

			cards.forEach(card => {
				card.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
				card.style.opacity = '0';
				card.style.transform = 'translateY(-10px)';
			});

			setTimeout(resolve, 200);
		});
	}

	/**
	 * Load posts via AJAX
	 * @param {string} category - Category slug
	 * @param {number} page - Page number
	 * @param {boolean} append - Append to existing posts or replace
	 */
	async function loadPosts(category, page, append) {
		if (isLoading) return;
		
		isLoading = true;

		// Preserve scroll position when appending
		const shouldPreserveScroll = append;
		const preservedScrollY = shouldPreserveScroll ? window.scrollY : null;
		
		// Show loading state
		if (loadMoreBtn) {
			loadMoreBtn.disabled = true;
			loadMoreBtn.classList.add('opacity-50');
		}
		
		if (!append) {
			await fadeOutCards();
		}

		try {
			const formData = new FormData();
			formData.append('action', 'load_markten_posts');
			formData.append('nonce', window.marktenAjax?.nonce || '');
			formData.append('category', category);
			formData.append('page', page);
			formData.append('per_page', loadMoreBtn?.dataset.perPage || 12);

			const response = await fetch(window.marktenAjax?.ajaxUrl || '/wp-admin/admin-ajax.php', {
				method: 'POST',
				body: formData
			});

			const data = await response.json();

			if (data.success) {
				if (append) {
					postsContainer.insertAdjacentHTML('beforeend', data.data.html);
				} else {
					postsContainer.innerHTML = data.data.html || '<p class="text-white/60 text-center col-span-3">Geen markten gevonden.</p>';
				}

				// Animate new cards
				const allCards = postsContainer.querySelectorAll('.markt-card');
				if (append) {
					// Only animate newly added cards
					const existingAnimated = postsContainer.querySelectorAll('.markt-card[style*="opacity: 1"]').length;
					const newCards = Array.from(allCards).slice(existingAnimated);
					animateCards(newCards, 0);
				} else {
					animateCards(allCards);
				}

				// Update load more button visibility
				if (loadMoreBtn) {
					loadMoreBtn.dataset.page = page;
					loadMoreBtn.dataset.category = category;
					
					if (data.data.has_more) {
						loadMoreBtn.closest('.markten-load-more').classList.remove('hidden');
					} else {
						loadMoreBtn.closest('.markten-load-more').classList.add('hidden');
					}
				}

				// Restore scroll position after DOM update (mobile)
				if (shouldPreserveScroll && preservedScrollY !== null) {
					requestAnimationFrame(() => {
						window.scrollTo({ top: preservedScrollY, behavior: 'auto' });
						if (loadMoreBtn) {
							loadMoreBtn.focus({ preventScroll: true });
						}
					});
				}
			}
		} catch (error) {
			console.error('Error loading posts:', error);
		} finally {
			isLoading = false;
			
			if (loadMoreBtn) {
				loadMoreBtn.disabled = false;
				loadMoreBtn.classList.remove('opacity-50');
			}
		}
	}
}

/**
 * Ensure a Swiper has enough slides for seamless `loop` mode.
 *
 * Swiper's loop needs at least ceil(maxSlidesPerView) * 2 real slides; with
 * fewer, its cloning can place identical slides next to each other (notably at
 * the loop seam). To avoid that we duplicate the WHOLE set as many times as
 * needed — appending whole copies (never a partial set) guarantees every
 * adjacency, including the seam, is "last → first" of distinct items.
 *
 * @param {Element} sliderEl   The Swiper root element.
 * @param {number}  maxPerView The largest slidesPerView across all breakpoints.
 */
function ensureLoopableSlides(sliderEl, maxPerView) {
	const wrapper = sliderEl.querySelector('.swiper-wrapper');
	if (!wrapper) return;

	const minSlides = Math.ceil(maxPerView) * 2;
	const originalSlides = Array.from(wrapper.children);
	const realCount = originalSlides.length;
	if (realCount === 0 || realCount >= minSlides) return;

	const copies = Math.ceil(minSlides / realCount) - 1;
	for (let c = 0; c < copies; c++) {
		originalSlides.forEach((slide) => {
			const clone = slide.cloneNode(true);
			clone.setAttribute('aria-hidden', 'true');
			wrapper.appendChild(clone);
		});
	}
}

/**
 * Initialize Markt Slider Gallery with Swiper
 */
function initSliderGalleryMarkt() {
	const sliderGallery = document.querySelector('.slider-gallery-markt');
	if (!sliderGallery) return;

	ensureLoopableSlides(sliderGallery, 2.75);

	new Swiper(sliderGallery, {
		modules: [Navigation],
		slidesPerView: 1.25,
		spaceBetween: 20,
		loop: true,
		navigation: {
			nextEl: '.slider-gallery-markt-navigation .swiper-button-next',
			prevEl: '.slider-gallery-markt-navigation .swiper-button-prev',
			addIcons: false,
		},
		breakpoints: {
			640: {
				slidesPerView: 1.75,
				spaceBetween: 20,
			},
			768: {
				slidesPerView: 2.75,
				spaceBetween: 24,
			},
		},
	});
}

/**
 * Initialize Tabbed Sliders functionality
 * Supports both tabbed version (with .tab-panel) and standalone slider (without tabs)
 */
function initTabbedSliders() {
	const tabbedSlidersSections = document.querySelectorAll('.tabbed-sliders');
	if (!tabbedSlidersSections.length) return;

	tabbedSlidersSections.forEach((section) => {
		const tabButtons = section.querySelectorAll('.tab-btn');
		const tabPanels = section.querySelectorAll('.tab-panel');
		const swiperInstances = [];

		// Check if this is a tabbed version or standalone slider
		const hasTabs = tabPanels.length > 0;

		if (hasTabs) {
			// Tabbed version: Initialize Swiper for each tab panel
			tabPanels.forEach((panel, index) => {
				const sliderEl = panel.querySelector('.tabbed-slider');
				if (!sliderEl) return;

				const swiper = new Swiper(sliderEl, {
					modules: [Navigation],
					slidesPerView: 1.2,
					spaceBetween: 16,
					centeredSlides: false,
					navigation: {
						nextEl: panel.querySelector('.tabbed-slider-navigation .swiper-button-next'),
						prevEl: panel.querySelector('.tabbed-slider-navigation .swiper-button-prev'),
						addIcons: false,
					},
					breakpoints: {
						640: {
							slidesPerView: 1.75,
						},
						1024: {
							slidesPerView: 3,
							spaceBetween: 0,
						},
					},
				});

				swiperInstances[index] = swiper;

				// Remove swiper default icons
				removeDefaultSwiperIcons(panel);
			});

			// Function to switch tabs
			const switchTab = (targetIndex) => {
				// Update active tab button (desktop)
				tabButtons.forEach((btn) => {
					btn.classList.remove('active');
					if (parseInt(btn.dataset.tabIndex, 10) === targetIndex) {
						btn.classList.add('active');
					}
				});

				// Show/hide tab panels
				tabPanels.forEach((panel) => {
					const panelIndex = parseInt(panel.dataset.tabPanel, 10);
					if (panelIndex === targetIndex) {
						panel.classList.remove('hidden');
						panel.classList.add('active');
						// First let Swiper recalc slide widths, then set slide-card height = width (square)
						requestAnimationFrame(() => {
							if (swiperInstances[panelIndex]) {
								swiperInstances[panelIndex].update();
							}
							requestAnimationFrame(() => updateSlideCardHeights(panel));
						});
					} else {
						panel.classList.add('hidden');
						panel.classList.remove('active');
					}
				});
			};

			// Tab buttons click handler (desktop)
			tabButtons.forEach((button) => {
				button.addEventListener('click', () => {
					const targetIndex = parseInt(button.dataset.tabIndex, 10);
					switchTab(targetIndex);
					
					// Update select value (mobile)
					const tabSelect = section.querySelector('.tab-select');
					if (tabSelect) {
						tabSelect.value = targetIndex;
					}
				});
			});

			// Tab select change handler (mobile)
			const tabSelect = section.querySelector('.tab-select');
			if (tabSelect) {
				const handleSelectChange = () => {
					const targetIndex = parseInt(tabSelect.value, 10);
					switchTab(targetIndex);
				};

				tabSelect.addEventListener('change', handleSelectChange);
				tabSelect.addEventListener('input', handleSelectChange);
			}
		} else {
			// Standalone slider (no tabs): Initialize single Swiper
			const sliderContainer = section.querySelector('.tabbed-slider-container');
			if (!sliderContainer) return;

			const sliderEl = sliderContainer.querySelector('.tabbed-slider');
			if (!sliderEl) return;

			new Swiper(sliderEl, {
				modules: [Navigation],
				slidesPerView: 1.2,
				spaceBetween: 16,
				centeredSlides: false,
				navigation: {
					nextEl: sliderContainer.querySelector('.tabbed-slider-navigation .swiper-button-next'),
					prevEl: sliderContainer.querySelector('.tabbed-slider-navigation .swiper-button-prev'),
					addIcons: false,
				},
				breakpoints: {
					640: {
						slidesPerView: 1.75,
					},
					1024: {
						slidesPerView: 3,
						spaceBetween: 0,
					},
				},
			});

			// Remove swiper default icons
			removeDefaultSwiperIcons(sliderContainer);
		}
	});

	/**
	 * Remove default Swiper navigation icons
	 * @param {HTMLElement} container - Container element
	 */
	function removeDefaultSwiperIcons(container) {
		const prevButton = container.querySelector('.swiper-button-prev');
		const nextButton = container.querySelector('.swiper-button-next');

		if (prevButton) {
			const swiperIcon = prevButton.querySelector('.swiper-navigation-icon');
			if (swiperIcon) swiperIcon.remove();
		}

		if (nextButton) {
			const swiperIcon = nextButton.querySelector('.swiper-navigation-icon');
			if (swiperIcon) swiperIcon.remove();
		}
	}

	/**
	 * Update slide-card heights (aspect-square) in a container. Used when panel becomes visible
	 * so cards get correct height after layout (hidden panels have offsetWidth 0 at init).
	 * @param {HTMLElement} container - Panel or section element
	 */
	function updateSlideCardHeights(container) {
		const slideCards = container.querySelectorAll('.slide-card.aspect-square');
		slideCards.forEach((card) => {
			const width = card.offsetWidth;
			card.style.height = width ? `${width}px` : '';
		});
	}

	/**
	 * Fix aspect-ratio for Safari and tabbed panels
	 * Safari doesn't properly support aspect-ratio inside flex containers.
	 * Only updates cards that are visible (in non-hidden panels) so hidden tab content doesn't get height: 0.
	 * @param {HTMLElement} section - Section element
	 */
	function fixAspectRatioForSafari(section) {
		const updateHeights = (container) => {
			if (!container) return;
			updateSlideCardHeights(container);
		};

		// For tabbed version: only set heights in visible panel(s)
		const tabPanels = section.querySelectorAll('.tab-panel');
		if (tabPanels.length) {
			tabPanels.forEach((panel) => {
				if (!panel.classList.contains('hidden')) {
					updateHeights(panel);
				}
			});
		} else {
			updateHeights(section);
		}

		// Update on window resize with debounce
		let resizeTimeout;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				if (tabPanels.length) {
					tabPanels.forEach((panel) => {
						if (!panel.classList.contains('hidden')) {
							updateHeights(panel);
						}
					});
				} else {
					updateHeights(section);
				}
			}, 100);
		});
	}

	// Apply aspect-ratio fix: only visible panels get heights set
	tabbedSlidersSections.forEach((section) => {
		fixAspectRatioForSafari(section);
	});
}

/**
 * Initialize Gallery Modal with Swiper (.slider-big-image)
 * Opens fullscreen modal on gallery image click in single-case.php
 */
function initGalleryModal() {
	const modal = document.getElementById('gallery-modal');
	const sliderEl = modal?.querySelector('.slider-big-image');

	if (!modal || !sliderEl) return;

	let gallerySwiper = null;

	// Make the slider focusable for better keyboard support (and accessibility).
	if (!sliderEl.hasAttribute('tabindex')) {
		sliderEl.setAttribute('tabindex', '0');
	}

	// Initialize Swiper
	gallerySwiper = new Swiper(sliderEl, {
		modules: [Navigation, Keyboard],
		slidesPerView: 1,
		spaceBetween: 0,
		loop: false,
		keyboard: {
			enabled: false, // enabled only while modal is open
			onlyInViewport: false,
		},
		navigation: {
			nextEl: '.slider-big-image-navigation .swiper-button-next',
			prevEl: '.slider-big-image-navigation .swiper-button-prev',
			addIcons: false,
		},
	});

	// Open modal function
	function openModal(index) {
		modal.classList.remove('hidden');
		modal.setAttribute('aria-hidden', 'false');
		document.body.classList.add('overflow-y-hidden');
		
		if (gallerySwiper) {
			gallerySwiper.slideTo(index, 0);
		}

		// Enable keyboard navigation only while modal is open.
		if (gallerySwiper?.keyboard?.enable) {
			gallerySwiper.keyboard.enable();
		}

		// Ensure focus is on the slider for Swiper keyboard handling.
		try {
			sliderEl.focus({ preventScroll: true });
		} catch (err) {
			// Fallback for older browsers that don't support preventScroll option.
			try {
				sliderEl.focus();
			} catch (e) {
				// no-op
			}
		}
	}

	// Close modal function
	function closeModal() {
		modal.classList.add('hidden');
		modal.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('overflow-y-hidden');

		// Disable to avoid interfering with page navigation when modal is closed.
		if (gallerySwiper?.keyboard?.disable) {
			gallerySwiper.keyboard.disable();
		}
	}

	// Click on gallery items or case-gallery-slider slides
	document.querySelectorAll('.gallery-item, .case-gallery-slider__slide').forEach((item) => {
		item.addEventListener('click', (e) => {
			e.preventDefault();
			const index = parseInt(item.getAttribute('data-index'), 10) || 0;
			openModal(index);
		});
	});

	// Close button
	const closeBtn = modal.querySelector('.gallery-modal-close');
	if (closeBtn) {
		closeBtn.addEventListener('click', closeModal);
	}

	// Click on overlay (outside slider)
	modal.addEventListener('click', (e) => {
		if (e.target === modal || e.target.classList.contains('gallery-modal-inner')) {
			closeModal();
		}
	});

	// ESC key to close
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
			closeModal();
		}
	});
}

/**
 * Update .case-gallery-slider__slide heights for Safari (aspect-ratio in flex is unreliable).
 * Uses aspect 1.2125 to match aspect-[1.2125]: height = width / 1.2125.
 * @param {HTMLElement} container - Section or slider container
 */
function updateCaseGallerySlideHeights(container) {
	const slides = container.querySelectorAll('.case-gallery-slider__slide');
	const aspect = 1.2125;
	slides.forEach((slide) => {
		const width = slide.offsetWidth;
		slide.style.height = width ? `${width / aspect}px` : '';
	});
}

/**
 * Initialize Case Gallery Slider (.case-gallery-slider) for new_style case layout
 */
function initCaseGallerySlider() {
	const sections = document.querySelectorAll('.case-gallery-slider-section');
	if (!sections.length) return;

	const swipers = [];

	sections.forEach((section) => {
		const sliderEl = section.querySelector('.case-gallery-slider');
		if (!sliderEl) return;

		// Make the slider focusable for keyboard support / accessibility.
		// Some environments require focus for keyboard handling.
		if (!sliderEl.hasAttribute('tabindex')) {
			sliderEl.setAttribute('tabindex', '0');
		}

		ensureLoopableSlides(sliderEl, 2.25);

		const swiper = new Swiper(sliderEl, {
			modules: [Navigation, Keyboard],
			slidesPerView: 1.33,
			spaceBetween: 20,
			centeredSlides: true,
			loop: true,
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},
			navigation: {
				nextEl: section.querySelector('.case-gallery-slider-next'),
				prevEl: section.querySelector('.case-gallery-slider-prev'),
				addIcons: false,
			},
			breakpoints: {
				640: {
					slidesPerView: 2,
				},
				768: {
					slidesPerView: 2.25,
					spaceBetween: 24,
				},
			},
		});

		swipers.push({ sliderEl, swiper });

		// Safari: set slide heights after Swiper has laid out (aspect-ratio in flex is broken)
		requestAnimationFrame(() => updateCaseGallerySlideHeights(section));
	});

	// Fallback keyboard handler (in case Swiper Keyboard doesn't react in your environment).
	// It only triggers for visible sliders and when user isn't typing.
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

		const active = document.activeElement;
		const tag = active?.tagName?.toLowerCase();
		const isEditable =
			active &&
			(active.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select');
		if (isEditable) return;

		const modal = document.getElementById('gallery-modal');
		if (modal && !modal.classList.contains('hidden')) return;

		if (!swipers.length) return;

		const viewportW = window.innerWidth || document.documentElement.clientWidth;
		const viewportH = window.innerHeight || document.documentElement.clientHeight;
		const centerX = viewportW / 2;
		const centerY = viewportH / 2;

		const visibleSwipers = swipers
			.map(({ sliderEl, swiper }) => {
				const rect = sliderEl.getBoundingClientRect();
				const inView =
					rect.bottom > 0 &&
					rect.right > 0 &&
					rect.top < viewportH &&
					rect.left < viewportW;
				return { sliderEl, swiper, rect, inView };
			})
			.filter((item) => item.inView);

		if (!visibleSwipers.length) return;

		visibleSwipers.sort((a, b) => {
			const aCx = a.rect.left + a.rect.width / 2;
			const aCy = a.rect.top + a.rect.height / 2;
			const bCx = b.rect.left + b.rect.width / 2;
			const bCy = b.rect.top + b.rect.height / 2;
			return (
				Math.abs(aCx - centerX) + Math.abs(aCy - centerY) -
				(Math.abs(bCx - centerX) + Math.abs(bCy - centerY))
			);
		});

		const { swiper } = visibleSwipers[0];
		e.preventDefault();
		if (e.key === 'ArrowLeft') swiper.slidePrev();
		else swiper.slideNext();
	});

	// Re-apply heights on resize
	let resizeTimeout;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimeout);
		resizeTimeout = setTimeout(() => {
			sections.forEach((section) => updateCaseGallerySlideHeights(section));
		}, 100);
	});
}

/**
 * Initialize Logos slider (.slider-logos) — autoplay, loop, no navigation/pagination
 */
function initLogosSlider() {
	const sliders = document.querySelectorAll('.slider-logos');
	if (!sliders.length) return;

	sliders.forEach((el) => {
		ensureLoopableSlides(el, 5);

		new Swiper(el, {
			modules: [Autoplay],
			slidesPerView: 2,
			spaceBetween: 24,
			loop: true,
			speed: 500,
			autoplay: {
				delay: 1500,
				disableOnInteraction: false,
			},
			breakpoints: {
				640: {
					slidesPerView: 3,
				},
				768: {
					slidesPerView: 4,
				},
				1024: {
					slidesPerView: 5,
				},
			},
		});
	});
}

/**
 * Initialize Collapsible Content functionality
 */
function initCollapsibleContent() {
	// Remove all existing listeners by cloning buttons
	const collapsibleButtons = document.querySelectorAll('.collapsible-toggle-btn');
	
	if (!collapsibleButtons.length) return;

	collapsibleButtons.forEach((button) => {
		// Check if button already has a listener (prevent duplicates)
		if (button.hasAttribute('data-listener-attached')) {
			// Clone button to remove all event listeners
			const newButton = button.cloneNode(true);
			button.parentNode.replaceChild(newButton, button);
			// Use the new button for the rest of the code
			button = newButton;
		}
		
		button.setAttribute('data-listener-attached', 'true');
		
		button.addEventListener('click', (e) => {
			e.stopPropagation(); // Prevent event bubbling
			
			const targetId = button.dataset.target;
			const hiddenContent = document.getElementById(targetId);
			const textMore = button.querySelector('.btn-text-more');
			const textLess = button.querySelector('.btn-text-less');

			if (!hiddenContent) return;

			// Toggle hidden content
			const isHidden = hiddenContent.classList.contains('hidden');
			
			if (isHidden) {
				hiddenContent.classList.remove('hidden');
				button.setAttribute('aria-expanded', 'true');
				if (textMore) textMore.classList.add('hidden');
				if (textLess) textLess.classList.remove('hidden');
			} else {
				hiddenContent.classList.add('hidden');
				button.setAttribute('aria-expanded', 'false');
				if (textMore) textMore.classList.remove('hidden');
				if (textLess) textLess.classList.add('hidden');
			}
		});
	});
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMain);
} else {
	// DOM is already ready
	initMain();
}
